// Shared wording and ordering for the badge collection, the profile showcase, the
// member shelf, the share page and the siteline feed. Plain functions, no React.
import { tierStyle } from "../art/badgeLib";
import { TIERS, type BadgeDef, type CategoryProgress, type Tier } from "../types";
import { roundValue } from "../value";

// ---------- families ----------

export const FAMILY_ORDER = ["aotd", "race", "coffee", "kitchen", "chat", "cards", "social", "community", "prints", "account"];

const FAMILY_LABEL: Record<string, string> = {
  aotd: "AOTD",
  race: "Race",
  coffee: "Coffee",
  kitchen: "Kitchen",
  chat: "Chat",
  cards: "Cards",
  social: "Social",
  community: "Community",
  prints: "Prints",
  account: "Account",
};

/** A flat accent per family, for shelf headers. */
const FAMILY_ACCENT: Record<string, string> = {
  aotd: "#60fac1",
  race: "#fa6089",
  coffee: "#ffe95d",
  kitchen: "#ffb36b",
  chat: "#22d6ff",
  cards: "#b69cff",
  social: "#ff9ecb",
  community: "#b7ffae",
  prints: "#ffc6e4",
  account: "#d9d9d9",
};

export function familyLabel(family: string): string {
  if (FAMILY_LABEL[family]) return FAMILY_LABEL[family];
  const f = (family || "other").replace(/[_-]+/g, " ");
  return f.charAt(0).toUpperCase() + f.slice(1);
}

export function familyAccent(family: string): string {
  return FAMILY_ACCENT[family] ?? "#f0f0f0";
}

export function familyRank(family: string): number {
  const i = FAMILY_ORDER.indexOf(family);
  return i < 0 ? FAMILY_ORDER.length : i;
}

// ---------- categories ----------

/** Category order inside a family shelf: the catalogue order, new categories after. */
const CATEGORY_ORDER = [
  "AOTD_NUMBER",
  "AOTD_COUNT",
  "AOTD_STREAK",
  "AOTD_FAST",
  "AOTD_TOP_DAYS",
  "AOTD_QUOTES",
  "AOTD_STREAK_COUNT",
  "RACE_WINS",
  "RACE_STREAK",
  "RACE_TIME",
  "GAME_JOINED",
  "COFFEE_NUMBER",
  "COFFEE_RATE_COUNT",
  "COFFEE_SHOPS",
  "HOME_BREW",
  "RECIPE_MAKE",
  "CHAT_MESSAGES",
  "VOICE_NOTES",
  "CARD_PLAYS",
  "CARD_LEGENDARY",
  "COMMENT_COUNT",
  "IDEA_SHIPPED",
  "PRINT_ORIGINAL",
  "PRINT_ON_WALLS",
  "PRINT_FAVORITE",
  "PRINT_CARDS",
  "PRINT_BOUGHT",
  "PRINT_POSTER",
  "SIGN_RANK",
  "ACCOUNT_AGE",
  "BADGE_COUNT",
];

export function categoryRank(category: string): number {
  const i = CATEGORY_ORDER.indexOf(category);
  return i < 0 ? CATEGORY_ORDER.length : i;
}

/** What a progress count is counting, singular and plural. */
const CATEGORY_UNIT: Record<string, [string, string]> = {
  AOTD_COUNT: ["AOTD", "AOTDs"],
  AOTD_QUOTES: ["quote", "quotes"],
  AOTD_STREAK: ["day in a row", "days in a row"],
  AOTD_FAST: ["quick draw", "quick draws"],
  AOTD_TOP_DAYS: ["day on top", "days on top"],
  RACE_WINS: ["win", "wins"],
  RACE_STREAK: ["morning in a row", "mornings in a row"],
  RECIPE_MAKE: ["recipe", "recipes"],
  COFFEE_RATE_COUNT: ["coffee", "coffees"],
  COFFEE_SHOPS: ["shop", "shops"],
  HOME_BREW: ["home brew", "home brews"],
  COMMENT_COUNT: ["comment", "comments"],
  CHAT_MESSAGES: ["message", "messages"],
  VOICE_NOTES: ["voice note", "voice notes"],
  CARD_PLAYS: ["card played", "cards played"],
  CARD_LEGENDARY: ["legendary pull", "legendary pulls"],
  IDEA_SHIPPED: ["idea shipped", "ideas shipped"],
  PRINT_ORIGINAL: ["original", "originals"],
  PRINT_ON_WALLS: ["print of your art", "prints of your art"],
  PRINT_CARDS: ["card", "cards"],
  PRINT_BOUGHT: ["print", "prints"],
  ACCOUNT_AGE: ["day", "days"],
  BADGE_COUNT: ["badge", "badges"],
};

/** "18 of 25 coffees" for a progress row. */
export function progressText(p: Pick<CategoryProgress, "category" | "current" | "next">): string {
  const target = p.next?.threshold ?? 0;
  const current = Math.max(0, Math.floor(p.current || 0));
  const unit = CATEGORY_UNIT[p.category];
  const noun = unit ? ` ${target === 1 ? unit[0] : unit[1]}` : "";
  return `${fmtInt(current)} of ${fmtInt(target)}${noun}`;
}

// ---------- tier, the badge's rarity ----------
//
// The tier (the colour of the art) is what a badge's rarity reads as on the site:
// "Ruby. 2 of 41 members have it." The engine's rarity classes still exist in the
// API (for the rank score and the sitebot) but the SPA never names them.

export function tierRank(tier: Tier | string): number {
  const i = TIERS.indexOf(tier as Tier);
  return i < 0 ? 0 : i;
}

/** "Ruby", "Gold", "Paper": the tier as the art names it. */
export function tierLabel(tier: Tier | string): string {
  const t = String(tier || "PAPER").toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** The tier's own colour, the fill of its base in the art. */
export function tierColor(tier: Tier | string): string {
  return tierStyle(String(tier)).fill;
}

/** "3 of 41 members have it" (empty when the numbers are missing). */
export function holdersText(holders: number, members: number): string {
  if (!(holders > 0) || !(members > 0)) return "";
  return `${fmtInt(holders)} of ${fmtInt(members)} ${members === 1 ? "member" : "members"} ${holders === 1 ? "has" : "have"} it`;
}

export interface HoldersInput {
  holders: number;
  members: number;
  /** Only one member can ever hold it. */
  unique?: boolean;
  /**
   * Whether the badge is held by whoever the words are about (the reader, or the
   * member whose shelf it is). Only a held badge can say "Nobody else has it."
   */
  held?: boolean;
}

/**
 * The holders fact: "Nobody else has it." for a badge only this member holds (or
 * a unique one), "2 of 41 members have it." otherwise. A badge nobody holds yet
 * says so; missing numbers give "".
 */
export function holdersFact({ holders, members, unique = false, held = true }: HoldersInput): string {
  if (held && (unique || (holders > 0 && holders <= 1))) return "Nobody else has it.";
  if (!held && holders === 0 && members > 0) return "Nobody has it yet.";
  const text = holdersText(holders, members);
  return text ? `${capitalize(text)}.` : "";
}

/** The lead of the line on a copy that was the first on the site. */
export const FIRST_ON_SITE = "First on the site.";

/** The stamp on a first-ever copy: the reveal card, the cinematic, the share story. */
export const FIRST_STAMP_TEXT = "First on n450s";

export interface TierLineInput extends HoldersInput {
  tier: Tier | string;
  /** Lead with "First on the site." */
  firstEver?: boolean;
}

/** The parts of a tier line; TierLineView draws the tier as a pill in its colour. */
export interface TierLine {
  lead: string | null;
  tier: string;
  /** "Ruby" */
  label: string;
  /** "2 of 41 members have it." (may be empty) */
  fact: string;
}

/** "Ruby. 2 of 41 members have it." / "First on the site. Gold. Nobody else has it." */
export function tierLine(input: TierLineInput): TierLine {
  return {
    lead: input.firstEver ? FIRST_ON_SITE : null,
    tier: String(input.tier || "PAPER").toUpperCase(),
    label: tierLabel(input.tier),
    fact: holdersFact(input),
  };
}

export function tierLineText(line: TierLine): string {
  return [line.lead, `${line.label}.`, line.fact].filter(Boolean).join(" ");
}

// ---------- numbers and dates ----------

export function fmtInt(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

/** Points can be fractional (18.85); show at most two decimals, no trailing zeros. */
export function fmtPoints(n: number): string {
  const r = roundValue(n || 0);
  return Number.isInteger(r) ? r.toLocaleString("en-US") : String(r);
}

export function pointsText(n: number): string {
  return `${fmtPoints(n)} ${Math.abs(n) === 1 ? "point" : "points"}`;
}

/** "Worth 21.38 points across 71 copies", for a badge held more than once. */
export function worthText(total: number, count: number): string {
  return `Worth ${pointsText(total)} across ${fmtInt(count)} ${count === 1 ? "copy" : "copies"}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "25 Sep 2026", in the viewer's time zone. Built by hand: Intl's short months
 * differ between engines ("Sep" or "Sept"), and the share image should not.
 */
export function fmtDate(iso: string | number | Date | null | undefined): string {
  if (iso === null || iso === undefined || iso === "") return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "1st", "2nd", "11th", "23rd". */
export function ordinal(n: number): string {
  const v = Math.abs(Math.floor(n));
  const mod100 = v % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${v}th`;
  switch (v % 10) {
    case 1:
      return `${v}st`;
    case 2:
      return `${v}nd`;
    case 3:
      return `${v}rd`;
    default:
      return `${v}th`;
  }
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// ---------- ordering ----------

/**
 * Best first: tier (the colour is the rarity), then the fewest holders, then
 * points. For "your three rarest" and the ranking's minis.
 */
export function compareRarest(
  a: Pick<BadgeDef, "rarity" | "tier" | "points">,
  b: Pick<BadgeDef, "rarity" | "tier" | "points">
): number {
  return (
    tierRank(b.tier) - tierRank(a.tier) ||
    (a.rarity?.pct ?? 100) - (b.rarity?.pct ?? 100) ||
    (b.points || 0) - (a.points || 0)
  );
}

/** Shelf order: category, then threshold (a category's tiers read left to right). */
export function compareShelf(
  a: Pick<BadgeDef, "category" | "threshold" | "code">,
  b: Pick<BadgeDef, "category" | "threshold" | "code">
): number {
  return (
    categoryRank(a.category) - categoryRank(b.category) ||
    a.category.localeCompare(b.category) ||
    (a.threshold || 0) - (b.threshold || 0) ||
    a.code.localeCompare(b.code)
  );
}

// ---------- pins ----------

export const MAX_PINS = 3;

/**
 * The pin list after pinning or unpinning `code`. Returns null when the member
 * already has MAX_PINS pinned and tries to pin another.
 */
export function nextPins(current: readonly string[], code: string, pin: boolean): string[] | null {
  const without = current.filter((c) => c !== code);
  if (!pin) return without;
  if (current.includes(code)) return [...current];
  if (without.length >= MAX_PINS) return null;
  return [...without, code];
}
